import { inject } from '@angular/core';
import { CanActivateFn, RouterStateSnapshot, ActivatedRouteSnapshot } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { OrgContextService } from './org-context.service';

/**
 * Auth guard that resolves the Auth0 org_id from the subdomain before
 * triggering login. Auth0 requires the org_id format (org_xxx), not a slug.
 *
 * Pattern mirrors Auth0's own AuthGuard.redirectIfUnauthenticated:
 *   - Uses isAuthenticated$ directly (it already waits for isLoading internally)
 *   - NO take(1) — the switchMap must stay alive so it reacts when
 *     isAuthenticated$ updates after the async auth0Client.isAuthenticated()
 *     call resolves post-callback. take(1) snaps a stale false and loops.
 *   - Passes appState.target so Auth0 returns the user to their original URL.
 */
export const orgAuthGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const auth = inject(AuthService);
  const orgContext = inject(OrgContextService);

  return auth.isAuthenticated$.pipe(
    tap(authenticated =>
      console.debug(`[OrgAuthGuard] isAuthenticated=${authenticated} url=${state.url}`)
    ),
    switchMap(authenticated => {
      if (authenticated) return of(true);

      // Resolve org context then redirect — fully chained, no fire-and-forget.
      // switchMap cancels this flow automatically if isAuthenticated$ emits
      // true before the resolve/redirect completes (e.g. fast token refresh).
      return orgContext.resolve().pipe(
        tap(ctx => console.debug('[OrgAuthGuard] Resolved ctx:', ctx)),
        switchMap(ctx =>
          auth.loginWithRedirect({
            authorizationParams: {
              ...(ctx?.orgId ? { organization: ctx.orgId } : {}),
            },
            // Preserve the intended destination so Auth0 returns here after login
            appState: { target: state.url },
          })
        ),
        map(() => false as const),
        catchError(err => {
          console.error('[OrgAuthGuard] Login redirect failed:', err);
          return of(false as const);
        }),
      );
    }),
  );
};
